// CRM 실 API 클라이언트 — /api/v2/crm/* + /api/v2/public-forms/*
//
// 서버 라우트: server/src/v2/routes/crm/*
// 스키마 SSOT: docs/projects/phone-board/spec/customer-schema.md
// 결정 근거: docs/projects/phone-board/spec/crm-decisions.md (D1 옵션 A 기준)

import { apiError, friendlyBodyError, publicFetch } from "./_error";
import { authFetch, authForm, authJson, buildAuthHeaders } from "./_client";
import { fetchPipelineCountsBatched, fetchStageCountsBatched } from "./crmBootstrap";
import { getCurrentActionId } from "../lib/perf-trace";
import type { CustomerFormConfig } from "../../shared/customer-form-config";
import type { RetailerFieldConfig } from "../../shared/field-registry";
import type { StoredSettlementConfig } from "../../shared/settlement-config";
import type {
  DealerSettlementLayoutConfig,
  StoredDealerSettlementLayout,
} from "../../shared/dealer-settlement-layout";
import type { DealerInvoiceConfirmation } from "../../shared/dealer-invoice-confirmation";
import type { WirelessConsultOptions } from "../../shared/wireless-consult-autofill";

import { applicationPageParams, type ApplicationPageFilter, type ApplicationPageMeta } from "../../shared/application-page";
export type { ApplicationPageFilter } from "../../shared/application-page";
export interface ApplicationPage extends ApplicationPageMeta { applications: ApplicationForm[] }
export function fetchApplicationPage(filter: ApplicationPageFilter, signal?: AbortSignal): Promise<ApplicationPage> {
  return fetchJson(`${API_V2}/crm/applications?${applicationPageParams(filter)}`, { signal });
}
export async function fetchApplicationPageIds(filter: ApplicationPageFilter): Promise<number[]> {
  const query = applicationPageParams(filter); query.set("idsOnly", "true");
  return (await fetchJson<{ids:number[]}>(`${API_V2}/crm/applications?${query}`)).ids;
}

const API_V2 = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2`;

// 미리보기 전용 썸네일 URL — 원본(카메라 5MB) 대신 서버가 줄인 webp 를 받는다.
// 화면 미리보기 화질만 양보하며, 저장값(idPhoto/cardPhoto)·인쇄(render.ts)는 원본 그대로다.
// http(s) S3 URL 일 때만 프록시로 감싼다(서명 data: URL·빈 값은 통과).
export function previewThumbUrl(src: string, width = 1200): string {
  if (!src || !/^https?:\/\//i.test(src)) return src;
  return `${API_V2}/image-thumb?src=${encodeURIComponent(src)}&w=${width}`;
}

// ─── 신분증 사진 펴기 (스크린챗 #459) ───────────────────────
// 출력 창이 열릴 때 서버가 사진에서 카드 네 모서리를 찾아 반듯한 직사각형으로 펴 준다.
// 원본 사진은 손대지 않는다 — 서버가 남기는 건 손으로 맞춘 모서리 좌표뿐이다.

export interface IdPhotoCorner {
  x: number;
  y: number;
}

export interface IdPhotoDewarpResult {
  /** 펴진 사진(dataURL). 모서리를 못 찾았으면 null — 화면은 원본을 띄운다. */
  image: string | null;
  /** 실제로 쓰인 네 모서리(0~1 정규 좌표, 시계방향 좌상→좌하). */
  corners: IdPhotoCorner[] | null;
  /** 펴 낸 카드를 똑바로 세우려고 적용한 회전각(0·90·180·270) — 스크린챗 #493. */
  rotation: number;
  /** 인쇄할 때 종이에 앉는 실물 폭(mm). 90·270 도로 세우면 짧은 변이 가로가 된다. */
  widthMm: number;
  /** manual=손으로 맞춤 · saved=기억해 둔 것 · auto=밝기 자동검출 · ai=AI 가 찾음 · none=못 찾음 */
  source: "manual" | "saved" | "auto" | "ai" | "none";
  /** true = 글자 방향을 아직 확인하지 않았다(화면이 mode:"orient" 로 한 번 더 부른다). */
  needsOrient: boolean;
}

export interface IdPhotoDewarpInput {
  customerId: number;
  src: string;
  /** 손으로 맞춘 네 모서리 — 주면 최우선이고 그대로 기억된다. */
  corners?: IdPhotoCorner[] | null;
  /** [↻ 돌리기] 로 정한 회전각. 주면 그 방향으로 펴고 기억한다. */
  rotate?: number | null;
  /**
   * fast(기본)=AI 를 안 부른다 · ai=기억을 무시하고 AI 가 새로 찾는다 ·
   * orient=펴 낸 카드를 AI 에게 보여 글자 방향만 정한다.
   */
  mode?: "fast" | "ai" | "orient";
}

export function dewarpIdPhoto(input: IdPhotoDewarpInput): Promise<IdPhotoDewarpResult> {
  return fetchJson<IdPhotoDewarpResult>(`${API_V2}/crm/id-photo/dewarp`, {
    method: "POST",
    body: JSON.stringify({
      customerId: input.customerId,
      src: input.src,
      corners: input.corners ?? null,
      rotate: input.rotate ?? null,
      mode: input.mode ?? "fast",
    }),
  });
}

function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  // body 가 있으면 Content-Type=application/json 자동 부착(기존 동작 = "truthy").
  return authJson<T>(url, init, "truthy");
}

export type CrmCarrier = "SKT" | "KT" | "LGU";
export type CrmStage = 1 | 2 | 3 | 4 | 5 | 6;
export type CrmPlanType = "신규" | "기변" | "번이";

// D6 (task 252) — 고객 입력·개통 파이프라인 1급 상태.
// SSOT: shared/pipeline-status.ts PIPELINE_STATUS.
export type PipelineStatus =
  | "not_started"
  | "partial_input"
  | "data_complete"
  | "id_received"
  | "form_submitted"
  | "activated"
  | "settled"
  | "canceled"
  | "expired"
  | "archived";

// ─── Customers ────────────────────────────────────────

export interface CrmCustomer {
  id: number;
  name: string;
  phone: string;
  address: string | null;
  email: string | null;
  emergencyPhone: string | null;
  shippingAddress: string | null;
  // task 238 — JSONB 통합 컬럼. 시스템·커스텀 필드 모두 {fieldKey: value} 누적.
  data?: Record<string, unknown>;
  // 등록 시점의 활성 입력폼 버전. null = 카탈로그 도입 이전 등록.
  formVersionId?: number | null;
  // 출처 (task 315) — 'external' 이면 엑셀·백업에서 가져온 고객. null 이면 자체 등록.
  // 상세 조회(/customers/:id)는 전 컬럼을 내려주므로 채워진다.
  source?: "external" | null;
  importBatchId?: string | null;
  // 문자 발송 정지(사람 기준 막기). null/과거 = 발송 가능, 미래 시각 = 그때까지 정지.
  suppressMessagesUntil?: string | null;
  // 하위 판매점 합산 표시 — 이 고객을 소유한 매장(테넌트). 부모 매장이 자식 건까지 모아 볼 때
  // "어느 판매점 고객인지" 표시·필터용. 목록 조회에서만 채워진다.
  ownerTenantId?: string;
  ownerTenantName?: string;
  // D8 (task 254): 파이프라인 상태는 신청서(ApplicationForm) 단위로 이전됨.
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CustomerFilter {
  search?: string;
  // 휴지통 모드 — true 면 isDeleted=true 인 고객만 조회.
  onlyDeleted?: boolean;
}

export async function fetchCustomers(filter: CustomerFilter = {}): Promise<CrmCustomer[]> {
  const qs = new URLSearchParams();
  if (filter.search) qs.set("search", filter.search);
  if (filter.onlyDeleted) qs.set("onlyDeleted", "true");
  const { customers } = await fetchJson<{ customers: CrmCustomer[] }>(
    `${API_V2}/crm/customers?${qs.toString()}`,
  );
  return customers;
}

// 휴지통에서 고객 복원 — 같이 cascade soft-delete 된 신청서도 함께 살아남.
export async function restoreCustomer(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/customers/${id}/restore`, { method: "POST" });
}

// ─── D8 — Pipeline 1급 상태 (신청서 단위) ────────────

export type PipelineCounts = Record<PipelineStatus, number> & {
  with_issue: number;
};

export function fetchPipelineCounts(): Promise<PipelineCounts> {
  // 화면 열기 묶음 경유 — 혼자 오면 원래 경로 그대로다(task 472).
  return fetchPipelineCountsBatched(() =>
    fetchJson<PipelineCounts>(`${API_V2}/crm/pipeline-counts`),
  );
}

// ─── task 312 — 파이프라인 단계 정의 (가운데 띠 커스텀) ──────────────
// 시작 2단계·마무리 4단계는 시스템 뼈대(코드 잠금), 가운데 단계는 매장이 자유 편집.
export type PipelineBand = "entry" | "middle" | "terminal";
export interface PipelineStage {
  // 가운데(DB) 단계만 id 를 가진다. 시스템 뼈대(entry/terminal)는 null.
  id: number | null;
  // application_forms.pipeline_status 에 저장되는 값.
  key: string;
  label: string;
  // 색 팔레트 토큰(gray·amber·blue·…). pipelineStageColors.ts 가 클래스로 변환.
  color: string;
  band: PipelineBand;
  isSystem: boolean;
  sortOrder: number;
}

// 시스템 뼈대 + 가운데 띠를 한 줄로 합친 "유효 단계 목록"(표시·정렬 순서대로).
export async function fetchPipelineStages(): Promise<PipelineStage[]> {
  const { stages } = await fetchJson<{ stages: PipelineStage[] }>(
    `${API_V2}/crm/pipeline-stages`,
  );
  return stages;
}

export async function createPipelineStage(input: {
  label: string;
  color?: string;
}): Promise<PipelineStage> {
  const { stage } = await fetchJson<{ stage: PipelineStage }>(
    `${API_V2}/crm/pipeline-stages`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return stage;
}

export async function updatePipelineStage(
  id: number,
  input: { label?: string; color?: string },
): Promise<PipelineStage> {
  const { stage } = await fetchJson<{ stage: PipelineStage }>(
    `${API_V2}/crm/pipeline-stages/${id}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return stage;
}

export async function reorderPipelineStages(ids: number[]): Promise<void> {
  await fetchJson(`${API_V2}/crm/pipeline-stages/reorder`, {
    method: "PUT",
    body: JSON.stringify({ ids }),
  });
}

// 삭제는 그 단계에 머문 신청서가 0건일 때만 성공한다(서버 가드, 409=STAGE_OCCUPIED).
export async function deletePipelineStage(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/pipeline-stages/${id}`, { method: "DELETE" });
}

export async function advancePipeline(
  applicationFormId: number,
  // task 312: 시스템 상태(PipelineStatus) 외에 커스텀 가운데 단계 키(cs_*)도 받으므로 string.
  to: string,
  options?: { clearSettledAt?: boolean; restoreArchived?: boolean },
): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/${applicationFormId}/pipeline/advance`, {
    method: "POST",
    body: JSON.stringify({
      to,
      clearSettledAt: options?.clearSettledAt === true,
      restoreArchived: options?.restoreArchived === true,
    }),
  });
}

// 일괄 상태 전이 — 선택한 신청서들을 한 요청으로 같은 상태(예: 보관 archived)로 옮긴다.
// 단건 advancePipeline 을 for 루프로 N번 부르던 것을 한 번의 요청으로 대체한다.
export async function bulkAdvancePipeline(
  ids: number[],
  to: string,
): Promise<{ updated: number }> {
  return fetchJson(`${API_V2}/crm/applications/bulk/advance`, {
    method: "POST",
    body: JSON.stringify({ ids, to }),
  });
}

export async function closeApplicationCard(applicationFormId: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/${applicationFormId}/pipeline/close`, {
    method: "POST",
    body: JSON.stringify({ reason: "canceled" }),
  });
}

export async function toggleApplicationIssue(
  applicationFormId: number,
  on: boolean,
  note?: string,
): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/${applicationFormId}/issue`, {
    method: "PUT",
    body: JSON.stringify({ on, note }),
  });
}

// 살아있는 토큰의 발급/만료 시각 조회. 토큰 원문은 응답 안 함(보안).
// 재발급은 issuePublicFormToken 사용.
export interface ActiveIntakeLinkInfo {
  activeTokenId: number;
  expiresAt: string;
  issuedAt: string;
}
export async function fetchActiveIntakeLink(
  applicationFormId: number,
): Promise<ActiveIntakeLinkInfo | null> {
  try {
    return await fetchJson<ActiveIntakeLinkInfo>(
      `${API_V2}/crm/applications/${applicationFormId}/intake-link`,
    );
  } catch (e) {
    // 405 = NO_ACTIVE_TOKEN. UI 에서 재발급 유도.
    if (e instanceof Error && /HTTP 405/.test(e.message)) return null;
    throw e;
  }
}

export type CrmCustomerDetail = CrmCustomer & {
  hasRrn: boolean;
  /** 복호화된 주민번호 평문. 미입력 시 null. 상세 조회에서만 내려온다. */
  rrn?: string | null;
};

export async function fetchCustomer(id: number): Promise<CrmCustomerDetail> {
  const { customer } = await fetchJson<{ customer: CrmCustomerDetail }>(
    `${API_V2}/crm/customers/${id}`,
  );
  return customer;
}

// 데모용 — 디테일 페이지에서 고객 정보를 샘플 값으로 채운다 (실 DB 박제).
// 정규 컬럼 + data jsonb 양쪽에 기록되므로 호출 후 ["crm"] invalidate 로 재조회.
export async function fillCustomerSample(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/customers/${id}/fill-sample`, { method: "POST" });
}

// task 255 (2026-05-24): 2단계 직원 확인·수정 모달에서 호출.
// body 의 키가 있으면 갱신, 없으면 유지(partial). 정규 컬럼 + data jsonb 동시 갱신.
// applicationFormId 가 주어지면 서버가 그 신청서를 not_started → partial_input 으로 자동 전이.
// (공개폼 제출 경로와 동일한 자동 전이 규칙. 다른 상태는 보존.)
export async function updateCustomerFormData(
  id: number,
  body: Record<string, unknown>,
  applicationFormId?: number | null,
  opts?: { keepalive?: boolean },
): Promise<void> {
  const payload =
    applicationFormId != null ? { ...body, applicationFormId } : body;
  await fetchJson(`${API_V2}/crm/customers/${id}/form-data`, {
    method: "PATCH",
    body: JSON.stringify(payload),
    // keepalive: 모달이 닫히거나 페이지를 떠나는 도중에도 이 저장 요청을 끝까지 보낸다.
    keepalive: opts?.keepalive,
  });
}

export async function softDeleteCustomer(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/customers/${id}`, { method: "DELETE" });
}

// ─── ApplicationForms ─────────────────────────────────

export interface ApplicationForm {
  id: number;
  customerId: number;
  // 목록 조회(/applications) 는 customers JOIN 으로 채워지고, 단건 조회
  // (/applications/by-customer, /applications/:id) 는 비워진다.
  customerName?: string;
  customerPhone?: string;
  carrier: CrmCarrier;
  planType: CrmPlanType | null;
  planName: string | null;
  deviceNameRaw: string | null;
  // 통신사 전송·공개폼으로 채워지는 개통 정규 컬럼. NUMERIC 류는 서버에서 문자열로 내려온다.
  // 단건 조회(/applications/:id) 응답엔 전 컬럼이 들어오며, RetailerInfoEditModal 이
  // retailer_info 가 비어 있을 때 이 값들을 대응 칸의 기본값으로 끌어온다.
  deviceColor?: string | null;
  factoryPrice?: string | null;
  officialSubsidy?: string | null;
  channelSubsidy?: string | null;
  stage: CrmStage;
  stageDetail: string | null;
  // 폼링크 4단계 상태머신(#200 · #206): '입력대기' 는 링크만 발급된 상태,
  // '입력중' 은 고객 제출 직후 매장 검토 대기, '확정' 은 검토 완료, '전송완료' 는 PDF 다운로드까지 끝.
  externalStatus: "입력대기" | "입력중" | "확정" | "전송완료";
  // 고객유형 (로드·약식·단순방문·가망고객 등 — retailer_info.customerType).
  // 목록 조회(/applications)에서만 채워진다.
  customerType?: string | null;
  // 매장 상품구분 값. 기존 커스텀 키 또는 정식 productCategory 키에서 읽으며,
  // 목록 조회(/applications)에서만 채워진다.
  productCategory?: string | null;
  // 통합 검색 매칭 필드 — 목록 조회(/applications)에서만 채워진다. 검색창 한 칸에서
  // 고객명·전화 외에 일련번호/IMEI·유심·요금제·단말기 모델·개통대리점으로도 찾기 위해
  // 서버가 함께 내려준다(imei 는 정규 컬럼, 나머지는 retailer_info 에서 추출).
  imei?: string | null;
  // 요금제는 정규 컬럼 planName 이 아니라 신청서 JSON 'plan' 에 입력된다(검색용 별도 필드).
  planRetail?: string | null;
  serialNo?: string | null;
  simNumber?: string | null;
  deviceModel?: string | null;
  dealer?: string | null;
  // 판매점이 신청서에 직접 입력한 실제 개통일(retailer_info.activatedAt). 아래 파이프라인
  // 전이 시각 activatedAt 과 이름이 겹치므로 별도 키로 받는다. 목록 조회에서만 채워진다.
  retailerActivatedAt?: string | null;
  // 정산완료 화면 전용 금액(#547) — 목록 조회(/applications)에서만 채워진다.
  // 페이백은 빈칸도 0, 기타 지원금은 빈칸이면 null(0원 입력과 구분 — 미반영 배지 판정용).
  paybackAmount?: number;
  otherSubsidyAmount?: number | null;
  // 매장 커스텀 항목 「대리점 정산 확인」. 항목이 없거나 선택하지 않았으면 null.
  settlementReviewStatus?: "matched" | "needs_review" | null;
  // D8 (task 254) — 파이프라인 1급 상태 + 사이드 플래그 + 단계별 timestamp (신청서 단위).
  // 목록 조회(/applications)에서만 채워진다.
  pipelineStatus?: PipelineStatus;
  hasIssue?: boolean;
  issueNote?: string | null;
  linkIssuedAt?: string | null;
  // 목록 조회 시 해당 신청서에 연결된 최신 single 입력 링크가 현재 시각 기준 만료됐는지.
  isLinkExpired?: boolean;
  firstInputAt?: string | null;
  dataCompletedAt?: string | null;
  idReceivedAt?: string | null;
  formSubmittedAt?: string | null;
  activatedAt?: string | null;
  settledAt?: string | null;
  closedAt?: string | null;
  closedReason?: string | null;
  createdAt: string;
  updatedAt: string;
  // 출처 (task 315) — 'external' 이면 엑셀·백업에서 가져온 신청서. null/undefined 면 자체 등록.
  // 목록 조회(/applications)에서만 채워진다(신청서 source 또는 고객 source 폴백).
  importSource?: "external" | "quote" | "business" | null;
  importSourceFileName?: string | null;
  importedAt?: string | null;
  // 하위 판매점 합산 표시 — 이 신청서를 소유한 매장(테넌트). 부모 매장이 자식들의 건까지
  // 한 화면에 모아 볼 때 "어느 판매점 건인지" 표시·필터하는 데 쓴다. 목록 조회에서만 채워진다.
  ownerTenantId?: string;
  ownerTenantName?: string;
  /** 자동 안내문자가 빈칸을 기다리는 중이면, 고객 목록 배지의 툴팁에 보여줄 항목명. */
  automaticMessageWaitingMissingLabels?: string[];
  // 판매점이 직접 입력하는 정보 묶음 (application_forms.retailer_info jsonb).
  // 키는 shared/retailer-form-fields.ts SSOT 의 field.key. 단건 조회와
  // by-customer 응답 모두에서 채워진다.
  retailerInfo?: Record<string, unknown> | null;
  // by-customer 경로에서만 채워진다. 자가입력으로 제출된 신청서는
  // publicFormTokens.submittedData (폼 body 원본) 가 들어온다.
  submittedData?: Record<string, unknown> | null;
  submittedAt?: string | null;
  confirmedAt?: string | null;
  deliveredAt?: string | null;
  // by-customer 응답은 applicationForms 전 컬럼을 내려준다. 서식 채우기 페이지(#174) 에서
  // 서명 썸네일 표시에 사용. 공개폼 미제출 신청서는 null.
  signatureUrl?: string | null;
}

export interface ApplicationFilter {
  stage?: CrmStage;
  carrier?: CrmCarrier;
  search?: string;
  // D8 — 파이프라인 상태 필터 ('active'/'terminal'/단일 enum 키) + 문제발생 필터.
  status?: PipelineStatus | "active" | "terminal";
  hasIssue?: boolean;
  // 휴지통 모드 — true 면 isDeleted=true 인 신청서만 조회.
  onlyDeleted?: boolean;
}

/** 목록 한 벌 + 서버 상한에서 잘렸는지. truncated 면 화면이 «일부만 보인다» 띠를 세운다. */
export interface ApplicationList {
  applications: ApplicationForm[];
  truncated: boolean;
  limit: number | null;
}

export async function fetchApplicationList(
  filter: ApplicationFilter = {},
): Promise<ApplicationList> {
  const qs = new URLSearchParams();
  if (filter.stage) qs.set("stage", String(filter.stage));
  if (filter.carrier) qs.set("carrier", filter.carrier);
  if (filter.search) qs.set("search", filter.search);
  if (filter.status) qs.set("status", filter.status);
  if (typeof filter.hasIssue === "boolean") qs.set("hasIssue", String(filter.hasIssue));
  if (filter.onlyDeleted) qs.set("onlyDeleted", "true");
  const body = await fetchJson<{
    applications: ApplicationForm[];
    truncated?: boolean;
    limit?: number;
  }>(`${API_V2}/crm/applications?${qs.toString()}`);
  return {
    applications: body.applications,
    truncated: body.truncated === true,
    limit: body.limit ?? null,
  };
}

/** 캐시를 거치지 않는 일회성 호출용. 화면 목록은 hooks 의 useApplications 를 쓴다. */
export async function fetchApplications(
  filter: ApplicationFilter = {},
): Promise<ApplicationForm[]> {
  return (await fetchApplicationList(filter)).applications;
}

// ─── 정산관리 (스크린챗 #86) ───────────────────────────────────────────
//
// "개통은 끝났는데 아직 정산 안 된 건"만 모은 목록. 건별 정산금·마진은 저장값이 아니라
// 계산값이라 서버가 신청서 모달·내보내기와 같은 계산 소스로 산출해 숫자만 내려준다.
export interface SettlementRow {
  id: number;
  customerId: number;
  customerName: string | null;
  customerPhone: string | null;
  carrier: CrmCarrier;
  dealer: string | null;
  model: string | null;
  plan: string | null;
  // 판매점이 신청서에 입력한 실개통일 ("YYYY-MM-DD..."). 월 집계 기준일.
  retailerActivatedAt: string | null;
  ownerTenantId: string;
  ownerTenantName: string | null;
  pipelineStatus: PipelineStatus;
  activatedAt: string | null;
  settledAt: string | null;
  createdAt: string;
  // 미입력 신청서는 null (화면에서 "—", 합계에서는 0).
  settlement: number | null;
  margin: number | null;
  // 고객정보의 마지막 현금수납/이체금액. 신청서마다 그대로 반복해 합산한다.
  cashAmount: number;
  // 신청서 "기타"의 표시 라벨이 페이백인 매장 입력 항목 값.
  paybackAmount: number;
  // 기타 지원금 합계(빌트인 otherSubsidy). 칸이 비어 있으면 null, 적혀 있으면 숫자(0 포함).
  // 페이백 미반영 판정(#547)은 "적었나"를 보므로 0 과 null 을 구분한다.
  otherSubsidyAmount: number | null;
  // 매장 커스텀 항목 「대리점 정산 확인」. 확인필요가 일치보다 우선한다.
  settlementReviewStatus: "matched" | "needs_review" | null;
}

export async function fetchSettlements(
  month: string,
): Promise<SettlementRow[]> {
  const { settlements } = await fetchJson<{ settlements: SettlementRow[] }>(
    `${API_V2}/crm/applications/settlements?month=${encodeURIComponent(month)}`,
  );
  return settlements;
}

// ─── 신청서 데이터 내보내기 (스프레드시트 다운로드) ────────────────────

// 한 신청서당 평면화된 항목 값 묶음. 고객 폼 항목 + 정규 컬럼 + 신청서 컬럼 + 메타('_' 접두).
export interface ApplicationExportRow {
  applicationFormId: number;
  values: Record<string, unknown>;
}

// 미리보기·카탈로그용 전체 행 조회. 실제 파일 생성은 서버(exceljs)가 한다.
export async function fetchApplicationsExport(): Promise<ApplicationExportRow[]> {
  const { applications } = await fetchJson<{ applications: ApplicationExportRow[] }>(
    `${API_V2}/crm/applications/export-data`,
  );
  return applications;
}

// 매장이 저장해 둔 내보내기 항목 구성(키 배열, 순서 포함). null = 저장 이력 없음 → 기본 선택.
export interface ExportColumnConfig {
  // null = 저장 이력 없음 → 프론트 기본 선택 사용.
  columns: string[] | null;
  // 미리보기 필터로 쓸 칼럼 키. null = 저장 이력 없음 → 자동 추천(범주형) 사용.
  filterColumns: string[] | null;
}

export async function fetchExportColumns(): Promise<ExportColumnConfig> {
  const res = await fetchJson<{
    columns: string[] | null;
    filterColumns?: string[] | null;
  }>(`${API_V2}/crm/applications/export-columns`);
  return { columns: res.columns, filterColumns: res.filterColumns ?? null };
}

// 내보내기 항목 구성을 매장(테넌트) 설정으로 저장 — 화면에서 바꿀 때마다 자동 호출된다.
// filterColumns 는 선택 — 안 넘기면 서버가 그 키를 건드리지 않는다.
export async function saveExportColumns(
  columns: string[],
  filterColumns?: string[],
): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/export-columns`, {
    method: "PUT",
    body: JSON.stringify({ columns, filterColumns }),
  });
}

// 고른 컬럼(키+라벨+순서)을 서버로 보내 .xlsx 바이너리를 받아 브라우저 다운로드를 띄운다.
export async function downloadApplicationsExport(
  columns: { key: string; label: string }[],
  filename: string,
  // 미리보기에 건 컬럼 필터 — 각 항목은 "이 컬럼의 표시값이 values 중 하나인 행만" 의미.
  // 비우면(기본) 전체 행을 받는다. 서버가 화면과 같은 포맷터로 같은 행을 골라낸다.
  filters: { key: string; values: string[] }[] = [],
  // 목록 화면(정산완료·개통완료·고객 관리)에서 걸러 둔 신청서 id — 주면 서버가 그 신청서만,
  // 보낸 순서 그대로 담는다. 안 주면(기본) 매장 전체 — 「데이터 내보내기」 화면의 예전 동작.
  applicationIds?: readonly number[],
): Promise<void> {
  const res = await authFetch(`${API_V2}/crm/applications/export-file`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      columns,
      filters,
      ...(applicationIds ? { applicationIds } : {}),
    }),
  });
  if (!res.ok) throw await apiError(res);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// 신청서 단위 휴지통/복원 — 한 줄(=신청서 1건) 단위로 처리. 고객 행은 보존된다.
export async function softDeleteApplication(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/${id}`, { method: "DELETE" });
}

export async function restoreApplication(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/${id}/restore`, { method: "POST" });
}

// 삭제 전 확인용 — 주어진 신청서들 중 고객이 작성 중인(살아있는) 입력 링크가 달린 id 만
// 가려낸다. 프론트는 이 결과로 삭제 확인 문구를 바꾼다(task 348).
export async function fetchApplicationsWithActiveLink(
  ids: number[],
): Promise<number[]> {
  if (ids.length === 0) return [];
  const { activeIds } = await fetchJson<{ activeIds: number[] }>(
    `${API_V2}/crm/applications/active-links`,
    { method: "POST", body: JSON.stringify({ ids }) },
  );
  return activeIds;
}

// 고객+신청서 통째 복제 — 고객까지 함께 베껴 원본과 끊어진 독립 사본(새 고객 + 새 신청서)을
// 만들고 그 id 들을 돌려준다. 진행상태·서명·확정 같은 생애주기 값은 서버에서 초기화된다.
export async function duplicateApplication(
  id: number,
): Promise<{ applicationFormId: number; customerId: number }> {
  return fetchJson(`${API_V2}/crm/applications/${id}/duplicate`, {
    method: "POST",
  });
}

export async function fetchApplicationsByCustomer(
  customerId: number,
): Promise<ApplicationForm[]> {
  const { applications } = await fetchJson<{ applications: ApplicationForm[] }>(
    `${API_V2}/crm/applications/by-customer/${customerId}`,
  );
  return applications;
}

// 신청서 단건 조회. 신청서 디테일 페이지가 URL 의 applicationFormId 만 갖고 있으므로
// 여기서 customerId 를 회수해 고객 정보·파이프라인 데이터를 후속 조회한다.
// 응답에는 customers JOIN 이 없어 customerName·pipelineStatus 는 비워질 수 있다.
export async function fetchApplication(id: number): Promise<ApplicationForm> {
  const { application } = await fetchJson<{ application: ApplicationForm }>(
    `${API_V2}/crm/applications/${id}`,
  );
  return application;
}

// 직원이 공개 폼 제출 없이 곧장 서식을 작성할 때, 비어 있는 신청서를 생성한다.
// 같은 고객에 활성 신청서가 이미 있으면 그것의 id 를 그대로 돌려준다.
export async function createApplicationForCustomer(
  customerId: number,
): Promise<{ applicationFormId: number; created: boolean }> {
  return fetchJson(`${API_V2}/crm/applications`, {
    method: "POST",
    body: JSON.stringify({ customerId }),
  });
}

// 판매점 정보 저장 — 전체 retailerInfo 객체를 통째로 갈아끼움 (서버에서 화이트리스트 필터링).
export async function updateRetailerInfo(
  applicationFormId: number,
  retailerInfo: Record<string, unknown>,
  opts?: { keepalive?: boolean },
): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/${applicationFormId}/retailer-info`, {
    method: "PUT",
    body: JSON.stringify({ retailerInfo }),
    // keepalive: 모달이 닫히거나 페이지를 떠나는 도중에도 이 저장 요청을 끝까지 보낸다.
    keepalive: opts?.keepalive,
  });
}

// 무선상담 자동완성처럼 신청서 일부만 확정하는 화면용. 서버가 DB jsonb 에 원자적으로
// 합쳐, 다른 신청서 창에서 입력 중인 나머지 값까지 오래된 전체 객체로 덮지 않는다.
export async function patchRetailerInfo(
  applicationFormId: number,
  patch: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const { retailerInfo } = await fetchJson<{
    retailerInfo: Record<string, unknown>;
  }>(`${API_V2}/crm/applications/${applicationFormId}/retailer-info`, {
    method: "PATCH",
    body: JSON.stringify({ patch }),
  });
  return retailerInfo;
}

/** 자동완성 고객값과 신청서값을 한 트랜잭션으로 저장한 뒤 렌더가 읽을 스냅샷을 확정한다. */
export async function saveWirelessConsultSnapshot(
  applicationFormId: number,
  customerPatch: Record<string, unknown>,
  retailerPatch: Record<string, unknown>,
): Promise<void> {
  await fetchJson(
    `${API_V2}/crm/applications/${applicationFormId}/wireless-consult-snapshot`,
    {
      method: "POST",
      body: JSON.stringify({ customerPatch, retailerPatch }),
    },
  );
}

// 잘못된 개통완료·정산완료를 한 번에 해제한다. 서버가 상태와 자동 예약문자를
// 한 트랜잭션으로 처리하므로, 화면은 별도 저장 요청을 섞지 않고 이 전용 동작만 호출한다.
export async function reopenApplicationActivation(
  applicationFormId: number,
): Promise<{ canceledAutomaticMessages: number }> {
  return fetchJson(
    `${API_V2}/crm/applications/${applicationFormId}/reopen-activation`,
    { method: "POST" },
  );
}

// 고객유형 일괄 변경 — 개통대기 현황에서 선택한 신청서들의 customerType 키만 지정/해제.
export async function bulkSetCustomerType(
  ids: number[],
  customerType: string | null,
): Promise<{ updated: number }> {
  return fetchJson(`${API_V2}/crm/applications/bulk/customer-type`, {
    method: "POST",
    body: JSON.stringify({ ids, customerType }),
  });
}

export async function updateApplicationStage(
  id: number,
  stage: CrmStage,
  stageDetail?: string,
): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/${id}/stage`, {
    method: "PATCH",
    body: JSON.stringify({ stage, stageDetail }),
  });
}

// 폼링크 4단계 상태머신 전이 API (#201). 자세한 계약은 tasks/201-폼링크-상태전이-API.md.
export async function confirmApplication(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/${id}/confirm`, { method: "POST" });
}

export async function unconfirmApplication(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/${id}/unconfirm`, { method: "POST" });
}

export async function markApplicationDelivered(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/applications/${id}/mark-delivered`, {
    method: "POST",
  });
}

export interface StageCount {
  stage: CrmStage;
  label: string;
  count: number;
}

export function fetchStageCounts(): Promise<StageCount[]> {
  // 같은 순간에 다른 화면 조회가 함께 오면 /crm/bootstrap 하나로 묶인다(task 472).
  // 혼자 오면(단계 편집 뒤 카운트만 갱신) 아래 원래 경로 그대로 나간다.
  return fetchStageCountsBatched(() =>
    fetchJson<{ stageCounts: StageCount[] }>(`${API_V2}/crm/applications/stage-counts`),
  );
}

// ─── 신청서 추이 (설정 및 데이터 > 신청서 추이) ───────────────

/** 하루×고객구분 원자 집계 한 칸. customerType 이 null 이면 구분 미지정 건. */
export interface ApplicationTrendRow {
  day: string; // YYYY-MM-DD
  customerType: string | null;
  count: number;
}

export async function fetchApplicationTrendStats(): Promise<
  ApplicationTrendRow[]
> {
  const { rows } = await fetchJson<{ rows: ApplicationTrendRow[] }>(
    `${API_V2}/crm/applications/trend-stats`,
  );
  return rows;
}

// ─── Public Forms (비인증 + 토큰 발급) ───────────────

export interface PublicFormView {
  tenantId: string;
  tenantName: string;
  customerName: string;
  phone: string;
  exposedFields: string[];
  requiredFields: string[];
  /** 매장이 설정한 고객 입력폼 모양 — 단순 폼이 이 설정대로 렌더한다. */
  customerFormConfig: CustomerFormConfig;
  /** 매장대표가 정한 새 자필서명 입력 펜 굵기(px). */
  signaturePenWidth: number;
  expiresAt: string;
  /**
   * single = 고객 1회용 링크(저장 대상이 이미 있음, 기존 동작).
   * kiosk = 매장 상설 QR — 모두가 공유하는 진입점이라 저장 대상이 없다.
   * kiosk 일 때만 "첫 입력 시 자식 토큰 발급(startPublicForm)" 흐름을 탄다.
   */
  kind: "single" | "kiosk" | "store";
  /**
   * 이어쓰기 프리필 — 고객이 이전에 입력하다 저장해 둔 값(fieldKey→값).
   * 창을 닫았다 새 링크로 다시 들어와도 입력하던 칸이 그대로 채워진다.
   * single 링크에만 채워지고(kiosk 는 빈 객체), 주민번호·서명도 고객이 이어서 작성할 수 있게 포함된다.
   */
  prefillData?: Record<string, string>;
  quotePrefilledKeys?: string[];
}

// 공개폼(비로그인)도 상관추적 번호표를 단다 — 고객 자가입력 여정이 run_id 없이 다니며
// 백엔드에서 제각각 fallback UUID를 받아 서로도 안 묶이던 추적 사각지대 해소(2026-07-05).
// 인증이 없어 _client(fetchWithAuthRetry)를 못 타므로 x-run-id·x-action-id만 경량으로 싣는다
// (백엔드 runId 미들웨어가 그대로 이어받아 로그·Sentry에 박는다).
async function publicFormHeaders(extra?: Record<string, string>): Promise<Headers> {
  // 고객용 링크에는 세션이 없어도 그대로 열리고, 판매점용 링크에는 같은 매장 로그인 세션이
  // Authorization 으로 전달돼야 한다. 세션이 없으면 헤더만 만든 채 보내므로 고객 흐름은 불변이다.
  const headers = await buildAuthHeaders({ headers: extra });
  headers.set("x-action-id", getCurrentActionId());
  return headers;
}

export async function fetchPublicForm(token: string): Promise<PublicFormView | null> {
  const res = await publicFetch(`${API_V2}/public-forms/${token}`, { headers: await publicFormHeaders() });
  if (res.status === 404) return null;
  // 410 = 만료/마감. 백엔드 안내 문구("마감된 링크입니다" 등)를 그대로 살려 화면에 정확히
  // 표시한다(task 348) — 삭제된 신청서의 링크를 연 고객이 "마감"임을 알 수 있게.
  if (res.status === 410) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(friendlyBodyError(body.error, "만료된 링크입니다"));
  }
  if (!res.ok) throw await apiError(res);
  const { token: t } = (await res.json()) as { token: PublicFormView };
  return t;
}

// 매장 QR(kiosk) 진입 → 이 손님 전용 자식 토큰을 발급받는다.
// kiosk 토큰은 모두가 공유하는 고정 진입점이라 그대로는 초안저장·입력중 배지가 안 된다
// (저장 대상이 없어 410). 손님이 "첫 입력"을 하는 순간 이 함수를 한 번 불러 single 자식
// 토큰을 받고, 이후 초안·하트비트·제출은 전부 자식 토큰으로 흘린다. 반환된 토큰은
// localStorage 에 캐시해 새로고침 시 재발급(=빈 카드 중복)을 막는다.
export async function startPublicForm(
  token: string,
): Promise<{ token: string; expiresAt: string }> {
  const res = await publicFetch(`${API_V2}/public-forms/${token}/start`, {
    method: "POST",
    headers: await publicFormHeaders(),
  });
  if (!res.ok) throw await apiError(res);
  return res.json();
}

// 공개 폼(비로그인)용 단말기 모델 카탈로그. 판매점 ModelCombobox(listPhoneModels)와
// 동일한 전역 카탈로그를 토큰 검증만으로 내려받아, 고객 모델명 선택값이 판매점
// 단말기(모델명) 입력값과 같은 표시명으로 일치(싱크)하도록 한다.
export async function fetchPublicFormModels(token: string): Promise<PhoneModel[]> {
  const res = await publicFetch(`${API_V2}/public-forms/${token}/models`, { headers: await publicFormHeaders() });
  if (!res.ok) throw await apiError(res);
  const { items } = (await res.json()) as { items: PhoneModel[] };
  return items;
}

export interface PublicFormSubmission {
  // 고객 정보
  name: string;
  phone: string;
  rrn?: string;
  address?: string;
  email?: string;
  emergencyPhone?: string;
  shippingAddress?: string;
  signatureUrl?: string;
  carrier?: CrmCarrier;
  // 자동이체 정보
  autopayInfo?: string;
  cardIssuer?: string;
  bankName?: string;
  accountNumber?: string;
  autopayHolder?: string;
  autopayRelation?: string;
  autopayBirth6?: string;
  autopayHolderPhone?: string;
  // 가입 정보
  currentCarrier?: string;
  desiredCarrier?: string;
  joinType?: string;
  desiredModel?: string;
  deviceCapacity?: string;
  // 구매 정보
  reportedPrice?: string;
  paymentMethod?: string;
  // 법정대리인
  guardianName?: string;
  guardianRelation?: string;
  guardianRrn?: string;
  guardianPhone?: string;
  // 동의·서명
  applicantName?: string;
  // task 255 신규 (2026-05-24)
  deliveryMethod?: string;
  autopayMethod?: string;
  desiredColor?: string;
  desiredColor2?: string;
  desiredColor3?: string;
  cashAmount?: string;
  idPhoto?: string;
  cardPhoto?: string;
}

// task 255 (2026-05-24): 신분증·카드 사진 업로드. 백엔드의 image-upload/:kind 라우트.
export async function uploadPublicFormImage(
  token: string,
  // 빌트인 "id-photo"|"card-photo" 외에 커스텀 사진 항목 키(cf_xxxx)도 같은 경로 사용.
  kind: string,
  file: File,
): Promise<{ key: string; url: string }> {
  const form = new FormData();
  form.append("image", file, file.name || `${kind}.jpg`);
  const res = await publicFetch(`${API_V2}/public-forms/${token}/image-upload/${kind}`, {
    method: "POST",
    headers: await publicFormHeaders(),
    body: form,
  });
  if (!res.ok) throw await apiError(res);
  return res.json();
}

// 신분증·카드 사진 업로드 (인증) — 판매점 직원이 "개통 신청서 수정" 모달에서 직접 올린다.
// uploadPublicFormImage 의 인증 버전. multipart 라 Content-Type 은 브라우저에 맡기고
// (fetchJson 을 안 쓰는 이유), Authorization 헤더만 세션 토큰으로 직접 싣는다.
export async function uploadCustomerImage(
  customerId: number,
  // 빌트인 "id-photo"|"card-photo" 외에 커스텀 사진 항목 키(cf_xxxx)도 같은 경로 사용.
  kind: string,
  file: File,
): Promise<{ key: string; url: string }> {
  const form = new FormData();
  form.append("image", file, file.name || `${kind}.jpg`);
  return authForm<{ key: string; url: string }>(
    `${API_V2}/crm/customers/${customerId}/image-upload/${kind}`,
    form,
  );
}

export interface CustomerImagePdfPreview {
  pageCount: number;
  /** 앞 10장까지의 가로 약 300px JPEG data URL. */
  previews: string[];
}

/** PDF 원본은 저장하지 않고 장수와 앞 10장의 작은 미리보기만 만든다(티켓 #924). */
export function previewCustomerImagePdf(
  customerId: number,
  pdf: File,
): Promise<CustomerImagePdfPreview> {
  const form = new FormData();
  form.append("pdf", pdf, pdf.name || "document.pdf");
  return authForm<CustomerImagePdfPreview>(
    `${API_V2}/crm/customers/${customerId}/image-pdf-preview`,
    form,
  );
}

/** 고른 PDF 한 장만 사진으로 바꾼다. 반환 파일은 기존 image-upload 경로에 그대로 올린다. */
export async function convertCustomerImagePdfPage(
  customerId: number,
  pdf: File,
  pageNumber: number,
): Promise<File> {
  const form = new FormData();
  form.append("pdf", pdf, pdf.name || "document.pdf");
  const res = await authFetch(
    `${API_V2}/crm/customers/${customerId}/image-pdf-page/${pageNumber}`,
    { method: "POST", body: form },
    120_000,
  );
  if (!res.ok) throw await apiError(res);
  const blob = await res.blob();
  const base = (pdf.name || "document.pdf").replace(/\.pdf$/i, "") || "document";
  return new File([blob], `${base}-${pageNumber}장.jpg`, { type: "image/jpeg" });
}

// 매장 공통 "서명" 커스텀 slot 의 손글씨 PNG 업로드 (인증 버전).
// uploadPublicFormSignature(공개폼·토큰) 의 관리자 짝 — 서식 상세 화면에서 매장이 직접
// 그린 서명을 매장 상수로 올린다. 반환 url 을 formDefaults[slug] 에 저장한다(미리보기·렌더 공용).
export async function uploadTenantFormSignature(
  blob: Blob,
): Promise<{ key: string; url: string }> {
  const form = new FormData();
  form.append("signature", blob, "signature.png");
  return authForm<{ key: string; url: string }>(
    `${API_V2}/crm/form-templates/signature-upload`,
    form,
  );
}

export async function uploadPublicFormSignature(
  token: string,
  blob: Blob,
): Promise<{ key: string; url: string }> {
  const form = new FormData();
  form.append("signature", blob, "signature.png");
  const res = await publicFetch(`${API_V2}/public-forms/${token}/signature-upload`, {
    method: "POST",
    headers: await publicFormHeaders(),
    body: form,
  });
  if (!res.ok) throw await apiError(res);
  return res.json();
}

export async function submitPublicForm(
  token: string,
  // 25필드 정식 폼(PublicFormSubmission) 과 단순 폼(평탄화된 설정 기반 값)
  // 양쪽을 받는다. 서버는 키를 submittedData jsonb 에 통째로 보존한다.
  data: PublicFormSubmission | Record<string, unknown>,
): Promise<{ customerId: number; applicationFormId: number }> {
  const res = await publicFetch(`${API_V2}/public-forms/${token}/submit`, {
    method: "POST",
    headers: await publicFormHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(data),
  });
  if (!res.ok) throw await apiError(res);
  return res.json();
}

/**
 * 비핵심 요청(초안·하트비트)의 결과 신호.
 *  - "ok"    : 정상 반영
 *  - "dead"  : 토큰이 죽었다(404 없음 / 410 만료·소진·마감) → 호출부가 캐시를 버리고 재발급해야 한다
 *  - "error" : 네트워크·서버 일시 오류 → 다음 시도가 만회하면 된다(토큰은 무죄)
 *
 * 왜 나눠 돌려주나: 매장 상설 QR(kiosk) 태블릿은 손님이 계속 바뀌는데, 브라우저에 캐시된
 * "지난 손님의 자식 토큰"이 죽어도 알아챌 길이 없었다. 서버는 초안 저장 때마다 410 으로
 * 알려주고 있었지만 여기서 통째로 삼켜, 손님이 다 적고 제출하는 마지막 순간에야 "만료된
 * 링크"가 드러났다. 그 신호를 살려 호출부가 조용히 자가 복구하게 한다.
 */
export type PublicFormPingResult = "ok" | "dead" | "error";

// 실시간 초안 저장 — 고객이 입력 도중 "바뀐 항목만" 부분 PATCH 로 보낸다(항목별 저장).
// best-effort: 실패해도 본 흐름(입력·제출)을 막지 않는다. 페이지 이탈 시에도 마지막 변경을
// 흘려보내려 keepalive 를 켠다. 토큰이 죽었으면(404·410) "dead" 로 알린다.
export async function savePublicFormDraft(
  token: string,
  partial: Record<string, unknown>,
): Promise<PublicFormPingResult> {
  try {
    const res = await publicFetch(`${API_V2}/public-forms/${token}/draft`, {
      method: "POST",
      headers: await publicFormHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify(partial),
      keepalive: true,
    });
    if (res.status === 404 || res.status === 410) return "dead";
    return res.ok ? "ok" : "error";
  } catch {
    // 초안은 비핵심 — 네트워크 오류는 다음 저장이 만회한다.
    return "error";
  }
}

// 접속 하트비트 — 고객 폼이 열려 있는 동안 주기적으로 호출해 "접속 중"을 알린다.
// 5초마다 도는 이 호출이 죽은 토큰을 가장 먼저 발견하는 감시자 역할도 겸한다.
export async function sendPublicFormHeartbeat(
  token: string,
): Promise<PublicFormPingResult> {
  try {
    const res = await publicFetch(`${API_V2}/public-forms/${token}/heartbeat`, {
      method: "POST",
      headers: await publicFormHeaders(),
      keepalive: true,
    });
    if (res.status === 404 || res.status === 410) return "dead";
    return res.ok ? "ok" : "error";
  } catch {
    // best-effort.
    return "error";
  }
}

/**
 * 토큰이 아직 살아 있는지만 가볍게 확인한다(캐시 검증용).
 *
 * 매장 태블릿이 페이지를 열 때, 브라우저에 캐시해 둔 자식 토큰이 그사이 죽지 않았는지
 * 먼저 물어본다. 죽었으면 손님이 한 글자도 적기 전에 버려, "다 적고 제출 눌렀더니 만료"를
 * 원천 차단한다. 판정 불가(네트워크 오류 등)는 true 로 둔다 — 멀쩡한 토큰을 오프라인이라는
 * 이유로 버리면 입력하던 손님의 이어쓰기가 날아가기 때문이다.
 */
export async function isPublicFormTokenAlive(token: string): Promise<boolean> {
  try {
    const res = await publicFetch(`${API_V2}/public-forms/${token}`, {
      headers: await publicFormHeaders(),
    });
    if (res.status === 404 || res.status === 410) return false;
    return true;
  } catch {
    return true;
  }
}

export async function issuePublicFormToken(input: {
  // 이름·전화번호는 고객이 폼에서 직접 입력하므로 발급 시엔 비워 보낸다(선택값).
  customerName?: string;
  phone?: string;
  exposedFields?: string[];
  requiredFields?: string[];
  expiresInHours?: number;
  // 판매점용은 시간 제한 없이, 해당 매장에 로그인한 직원만 여는 링크다.
  kind?: "single" | "store";
  // 지정 시: 새 신청서를 만들지 않고 이 기존 신청서에 새 토큰을 묶어 재발급(디테일 재복사용).
  applicationFormId?: number;
}): Promise<{ id: number; token: string; expiresAt: string }> {
  return fetchJson(`${API_V2}/public-form-tokens`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

// ─── 매장 상설 QR(kiosk) 토큰 ───────────────────────────
// 매장 벽에 인쇄해 두는 QR 하나. 손님이 스캔할 때마다 새 고객 카드가 생긴다.
// 원문 토큰을 서버가 보관하므로(공개 전시 토큰이라 무방) 매번 같은 QR 을 다시 띄울 수 있다.

// 현재 매장에 활성 QR 이 있는지 + 발급일 + 원문 토큰. token 이 있으면 그대로 같은 QR 을 띄운다.
// (옛 발급 건은 원문이 없어 token 이 안 옴 → 한 번 재발급하면 이후로 고정된다.)
export async function fetchKioskFormStatus(): Promise<{
  active: boolean;
  issuedAt?: string;
  token?: string;
}> {
  return fetchJson(`${API_V2}/public-form-tokens/kiosk`);
}

// 매장 상설 QR 발급(= 재발급). 기존 활성 QR 은 자동 폐기되고 새 평문 토큰을 돌려준다.
export async function issueKioskFormToken(): Promise<{
  token: string;
  expiresAt: string;
}> {
  return fetchJson(`${API_V2}/public-form-tokens/kiosk`, { method: "POST" });
}

// 매장 상설 QR 폐기 — 인쇄물을 더 못 쓰게 막는다.
export async function revokeKioskFormToken(): Promise<{ ok: boolean }> {
  return fetchJson(`${API_V2}/public-form-tokens/kiosk`, { method: "DELETE" });
}

// ─── 매장 태블릿 서명 패드 ───────────────────────────

export type SignaturePadFieldKey = "nameSign" | "noticeSign" | "signature";
export type SignaturePadFieldMap = Record<SignaturePadFieldKey, string>;

export interface SignaturePadSession {
  id: string;
  customerId: number;
  applicationFormId: number;
  status: "active" | "completed" | "cancelled" | "superseded" | "expired";
  guides: SignaturePadFieldMap;
  result: SignaturePadFieldMap | null;
  expiresAt: string;
}

export interface PublicSignaturePadSession {
  id: string;
  status: "active";
  guides: SignaturePadFieldMap;
  expiresAt: string;
}

export type PublicSignaturePadConnection =
  | {
      state: "connected";
      session: PublicSignaturePadSession | null;
      channel: string;
      signaturePenWidth: number;
    }
  | { state: "revoked" };

// ─── 태블릿 연결 QR 관리 (로그인 필요, 매장 상설 QR과 같은 접근 범위) ───

export async function fetchSignaturePadTokenStatus(): Promise<{
  active: boolean;
  token?: string;
  issuedAt?: string;
}> {
  return fetchJson(`${API_V2}/signature-pad-tokens`);
}

export async function issueSignaturePadToken(): Promise<{
  token: string;
  issuedAt: string;
}> {
  return fetchJson(`${API_V2}/signature-pad-tokens`, { method: "POST" });
}

// ─── 공개 태블릿 경로 (로그인 없음, 비밀 주소 토큰이 입장권) ───

export async function fetchPublicSignaturePadSession(
  token: string,
): Promise<PublicSignaturePadConnection> {
  const res = await publicFetch(`${API_V2}/public-signature-pad/${token}/session`, {
    headers: await publicFormHeaders(),
  });
  if (res.status === 404 || res.status === 410) return { state: "revoked" };
  if (!res.ok) throw await apiError(res);
  const data = (await res.json()) as Omit<
    Extract<PublicSignaturePadConnection, { state: "connected" }>,
    "state"
  >;
  return { state: "connected", ...data };
}

export async function uploadPublicSignaturePadImage(
  token: string,
  sessionId: string,
  kind: SignaturePadFieldKey,
  file: File,
): Promise<{ key: string; url: string }> {
  const form = new FormData();
  form.append("image", file, file.name || `${kind}.png`);
  const res = await publicFetch(
    `${API_V2}/public-signature-pad/${token}/sessions/${sessionId}/image-upload/${kind}`,
    {
      method: "POST",
      headers: await publicFormHeaders(),
      body: form,
    },
  );
  if (!res.ok) throw await apiError(res);
  return res.json();
}

export async function completePublicSignaturePadSession(
  token: string,
  sessionId: string,
  signatures: SignaturePadFieldMap,
): Promise<{ ok: true }> {
  const res = await publicFetch(
    `${API_V2}/public-signature-pad/${token}/sessions/${sessionId}/complete`,
    {
      method: "POST",
      headers: await publicFormHeaders({ "Content-Type": "application/json" }),
      body: JSON.stringify({ signatures }),
    },
  );
  if (!res.ok) throw await apiError(res);
  return res.json();
}

export async function fetchActiveSignaturePadSession(): Promise<{
  session: SignaturePadSession | null;
}> {
  return fetchJson(`${API_V2}/crm/signature-pad/session`);
}

export async function fetchSignaturePadSession(id: string): Promise<{
  session: SignaturePadSession;
}> {
  return fetchJson(`${API_V2}/crm/signature-pad/sessions/${id}`);
}

export async function startSignaturePadSession(input: {
  customerId: number;
  applicationFormId: number;
  values: { rrn?: string; name?: string; guardianName?: string };
}): Promise<{ session: SignaturePadSession }> {
  return fetchJson(`${API_V2}/crm/signature-pad/sessions`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function cancelSignaturePadSession(id: string): Promise<{ ok: boolean }> {
  return fetchJson(`${API_V2}/crm/signature-pad/sessions/${id}/cancel`, {
    method: "POST",
  });
}

export async function completeSignaturePadSession(
  id: string,
  signatures: SignaturePadFieldMap,
): Promise<{ ok: boolean }> {
  return fetchJson(`${API_V2}/crm/signature-pad/sessions/${id}/complete`, {
    method: "POST",
    body: JSON.stringify({ signatures }),
  });
}

// ─── Dashboard Memos ──────────────────────────────────

export interface DashboardMemo {
  id: number;
  priority: "urgent" | "high" | "medium" | "low";
  title: string;
  subtitle: string | null;
  content: string | null;
  customerId: number | null;
  dueDate: string | null;
  createdAt: string;
}

export async function fetchDashboardMemos(): Promise<DashboardMemo[]> {
  const { memos } = await fetchJson<{ memos: DashboardMemo[] }>(
    `${API_V2}/crm/dashboard-memos`,
  );
  return memos;
}

export async function createDashboardMemo(input: {
  priority?: "urgent" | "high" | "medium" | "low";
  title: string;
  subtitle?: string;
  content?: string;
  customerId?: number;
  dueDate?: string | null;
}): Promise<{ id: number }> {
  return fetchJson(`${API_V2}/crm/dashboard-memos`, {
    method: "POST",
    body: JSON.stringify(input),
  });
}

export async function deleteDashboardMemo(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/dashboard-memos/${id}`, { method: "DELETE" });
}

// ─── 고객 입력폼 설정 (SSOT: tenants.customer_form_config jsonb) ──

export async function fetchCustomerFormConfig(): Promise<CustomerFormConfig> {
  const { config } = await fetchJson<{ config: CustomerFormConfig }>(
    `${API_V2}/crm/customer-form-config`,
  );
  return config;
}

export async function saveCustomerFormConfig(
  config: CustomerFormConfig,
): Promise<void> {
  await fetchJson(`${API_V2}/crm/customer-form-config`, {
    method: "PUT",
    body: JSON.stringify(config),
  });
}

/** 고객 입력폼 설정 변경 이력 한 줄 — 저장 시점의 통짜 스냅샷 + 누가·언제. */
export interface FormConfigHistoryItem {
  id: number;
  snapshot: CustomerFormConfig;
  note: string | null;
  createdAt: string;
  createdBy: string | null;
  createdByName: string | null;
}

/** 변경 이력 목록(최신순) + 현재 사용자가 되돌리기를 할 수 있는지(canRevert=매장대표/본사). */
export async function fetchCustomerFormConfigHistory(): Promise<{
  history: FormConfigHistoryItem[];
  canRevert: boolean;
}> {
  return fetchJson(`${API_V2}/crm/customer-form-config/history`);
}

/** 과거 스냅샷(id)을 현재 설정으로 되살린다 — 서버가 owner 권한을 강제한다. */
export async function rollbackCustomerFormConfig(
  id: number,
): Promise<{ ok: boolean; config: CustomerFormConfig }> {
  return fetchJson(`${API_V2}/crm/customer-form-config/rollback/${id}`, {
    method: "POST",
  });
}

// ─── 매장 입력 항목 설정 (SSOT: tenants.retailer_field_config jsonb, task 256) ──

export async function fetchRetailerFieldConfig(): Promise<RetailerFieldConfig> {
  const { config } = await fetchJson<{ config: RetailerFieldConfig }>(
    `${API_V2}/crm/retailer-field-config`,
  );
  return config;
}

export async function saveRetailerFieldConfig(
  config: RetailerFieldConfig,
): Promise<void> {
  await fetchJson(`${API_V2}/crm/retailer-field-config`, {
    method: "PUT",
    body: JSON.stringify(config),
  });
}

// ─── 판매점 정산식 커스텀 (SSOT: tenants.settlement_config jsonb, task 268) ──

export async function fetchSettlementConfig(): Promise<StoredSettlementConfig> {
  const { config } = await fetchJson<{ config: StoredSettlementConfig }>(
    `${API_V2}/crm/settlement-config`,
  );
  return config;
}

export async function saveSettlementConfig(
  config: StoredSettlementConfig,
): Promise<void> {
  await fetchJson(`${API_V2}/crm/settlement-config`, {
    method: "PUT",
    body: JSON.stringify(config),
  });
}

// ─── 정산관리 거래 대리점 배치 (ScreenChat #735) ──────────────

export async function fetchDealerSettlementLayout(): Promise<StoredDealerSettlementLayout> {
  const { layout } = await fetchJson<{ layout: StoredDealerSettlementLayout }>(
    `${API_V2}/crm/dealer-settlement-layout`,
  );
  return layout;
}

export async function saveDealerSettlementLayout(
  layout: DealerSettlementLayoutConfig,
): Promise<DealerSettlementLayoutConfig> {
  const result = await fetchJson<{ layout: DealerSettlementLayoutConfig }>(
    `${API_V2}/crm/dealer-settlement-layout`,
    { method: "PUT", body: JSON.stringify(layout) },
  );
  return result.layout;
}

// ─── 정산관리 대리점별 계산서 발행확인 (ScreenChat #891) ──────────────

export async function fetchDealerInvoiceConfirmations(
  month: string,
): Promise<DealerInvoiceConfirmation[]> {
  const params = new URLSearchParams({ month });
  const { confirmations } = await fetchJson<{
    confirmations: DealerInvoiceConfirmation[];
  }>(`${API_V2}/crm/dealer-invoice-confirmations?${params.toString()}`);
  return confirmations;
}

export async function setDealerInvoiceConfirmation(input: {
  month: string;
  dealerName: string;
  confirmed: boolean;
}): Promise<DealerInvoiceConfirmation | null> {
  const { confirmation } = await fetchJson<{
    confirmation: DealerInvoiceConfirmation | null;
  }>(`${API_V2}/crm/dealer-invoice-confirmations`, {
    method: input.confirmed ? "PUT" : "DELETE",
    body: JSON.stringify({ month: input.month, dealerName: input.dealerName }),
  });
  return confirmation;
}

// ─── task 240 — 텍스트 자동변환 템플릿 API ──────────────

export interface TextTemplate {
  id: number;
  tenantId: string;
  name: string;
  body: string;
  // 티켓 #421 — 신청서 화면 [기본 안내문자 보내기]가 꺼내 쓰는 문구 지정(매장당 1개).
  isDefaultNotice: boolean;
  isDeleted: boolean;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TextTemplateVersion {
  id: number;
  textTemplateId: number;
  versionNumber: number;
  snapshot: { name: string; body: string };
  isActive: boolean;
  createdBy: string | null;
  createdAt: string;
}

export async function listTextTemplates(): Promise<TextTemplate[]> {
  const { templates } = await fetchJson<{ templates: TextTemplate[] }>(
    `${API_V2}/crm/text-templates`,
  );
  return templates;
}

export async function createTextTemplate(input: {
  name: string;
  body: string;
}): Promise<TextTemplate> {
  const { template } = await fetchJson<{ template: TextTemplate }>(
    `${API_V2}/crm/text-templates`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return template;
}

export async function updateTextTemplate(
  id: number,
  input: { name: string; body: string },
): Promise<TextTemplate> {
  const { template } = await fetchJson<{ template: TextTemplate }>(
    `${API_V2}/crm/text-templates/${id}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return template;
}

export async function deleteTextTemplate(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/text-templates/${id}`, { method: "DELETE" });
}

// 기본 안내문자 지정/해제 — 켜면 같은 매장의 다른 지정은 서버가 함께 끈다.
export async function setDefaultNoticeTemplate(
  id: number,
  on: boolean,
): Promise<TextTemplate> {
  const { template } = await fetchJson<{ template: TextTemplate }>(
    `${API_V2}/crm/text-templates/${id}/default-notice`,
    { method: "POST", body: JSON.stringify({ on }) },
  );
  return template;
}

export async function listTextTemplateVersions(
  id: number,
): Promise<TextTemplateVersion[]> {
  const { versions } = await fetchJson<{ versions: TextTemplateVersion[] }>(
    `${API_V2}/crm/text-templates/${id}/versions`,
  );
  return versions;
}

export async function restoreTextTemplateVersion(
  id: number,
  versionId: number,
): Promise<TextTemplate> {
  const { template } = await fetchJson<{ template: TextTemplate }>(
    `${API_V2}/crm/text-templates/${id}/versions/${versionId}/restore`,
    { method: "POST" },
  );
  return template;
}

/**
 * 개통일이 없어 해지가능일·요금제 변경 가능일을 계산하지 못한 항목이 이 템플릿에 있을 때의 신호.
 * (예전엔 오늘 날짜로 대체했으나, 실제와 다른 날짜가 나가는 사고를 막으려 대체를 없애고 입력을 유도한다.)
 */
export interface ActivationRequired {
  /** 개통일이 없어 날짜를 못 넣은 항목 라벨 (예: "부가서비스 해지가능일"). */
  fields: string[];
  /** 개통일을 저장할 대상 신청서 id (그 자리에서 개통일 입력·저장용). null이면 신청서가 없음. */
  applicationFormId: number | null;
  /** 현재 신청서 retailer_info 전체 — 개통일만 덮어써 통째로 교체 저장하기 위함. */
  retailerInfo: Record<string, unknown>;
}

export interface RenderedTextTemplate {
  text: string;
  /** 템플릿이 참조하지만 이 신청서에 값이 없는 항목의 사람용 라벨. */
  missingFields: string[];
  /** 템플릿이 참조하는 신청서 원본 칸 키. 값이 비어 있어도 포함한다. */
  referencedFieldKeys: string[];
  /** null 이면 개통일 관련 미입력 항목 없음. */
  activationRequired: ActivationRequired | null;
}

/** 특정 고객의 jsonb 데이터로 템플릿 치환 결과 반환 (서버에서 머지). */
export async function renderTextTemplate(
  id: number,
  customerId: number,
  applicationFormId?: number,
): Promise<RenderedTextTemplate> {
  const { text, missingFields, referencedFieldKeys, activationRequired } =
    await fetchJson<{
      text: string;
      missingFields?: string[];
      referencedFieldKeys?: string[];
      activationRequired: ActivationRequired | null;
    }>(`${API_V2}/crm/text-templates/${id}/render`, {
    method: "POST",
    body: JSON.stringify({ customerId, applicationFormId }),
  });
  return {
    text,
    missingFields: missingFields ?? [],
    referencedFieldKeys: referencedFieldKeys ?? [],
    activationRequired: activationRequired ?? null,
  };
}

/**
 * 특정 고객의 "항목별 실제 값" 맵 반환 (스크린챗 #13) — 텍스트 자동변환 우측 패널이
 * 각 항목 옆에 그 고객의 실제 값을 함께 보여주는 데 쓴다. keys 를 주면 그 항목만 채운다.
 */
export async function fetchTextTemplateFieldValues(
  customerId: number,
  keys?: string[],
): Promise<Record<string, string>> {
  const { values } = await fetchJson<{ values: Record<string, string> }>(
    `${API_V2}/crm/text-templates/field-values`,
    {
      method: "POST",
      body: JSON.stringify({ customerId, keys }),
    },
  );
  return values ?? {};
}

/** 매장의 예시값 오버라이드 조회 (스크린챗 #13). 기본 예시와 다른 항목만 담긴다. */
export async function fetchTextTemplateSampleData(): Promise<
  Record<string, string>
> {
  const { overrides } = await fetchJson<{ overrides: Record<string, string> }>(
    `${API_V2}/crm/text-templates/sample-data`,
  );
  return overrides ?? {};
}

/** 매장의 예시값 오버라이드 저장 (스크린챗 #13). 빈 문자열은 서버가 버린다. */
export async function saveTextTemplateSampleData(
  overrides: Record<string, string>,
): Promise<Record<string, string>> {
  const { overrides: saved } = await fetchJson<{
    overrides: Record<string, string>;
  }>(`${API_V2}/crm/text-templates/sample-data`, {
    method: "PUT",
    body: JSON.stringify({ overrides }),
  });
  return saved ?? {};
}

// ─── 매장 변수 API (dealer-variables) ───────────────────

export interface DealerVariable {
  id: number;
  tenantId: string;
  key: string;
  label: string;
  value: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export async function listDealerVariables(): Promise<DealerVariable[]> {
  const { variables } = await fetchJson<{ variables: DealerVariable[] }>(
    `${API_V2}/crm/dealer-variables`,
  );
  return variables;
}

export async function createDealerVariable(input: {
  key: string;
  label?: string;
  value?: string;
}): Promise<DealerVariable> {
  const { variable } = await fetchJson<{ variable: DealerVariable }>(
    `${API_V2}/crm/dealer-variables`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return variable;
}

export async function updateDealerVariable(
  id: number,
  input: { key?: string; label?: string; value?: string; sortOrder?: number },
): Promise<DealerVariable> {
  const { variable } = await fetchJson<{ variable: DealerVariable }>(
    `${API_V2}/crm/dealer-variables/${id}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return variable;
}

export async function deleteDealerVariable(id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/dealer-variables/${id}`, { method: "DELETE" });
}

// ─── 마스터 데이터 API (task 258) ─────────────────────────────────
//
// kind: dealer | addon | welfare | memo
// GET    /crm/master/:kind
// POST   /crm/master/:kind
// PUT    /crm/master/:kind/:id
// DELETE /crm/master/:kind/:id

export type MasterKind =
  | "dealer"
  | "addon"
  | "welfare"
  | "memo"
  | "insurance"
  | "mvno"
  | "plan"
  | "paymentMethod"
  | "customerType"
  | "usedSimActivation"
  | "productCategory";

export interface DealerMaster {
  id: number;
  tenantId: string;
  name: string;
  alias: string | null;
  /** 화면 표시용 — 업체 원장(/policy/vendors) 별칭 → 원장 업체명 → name 순.
   *  신청서에 저장되는 값은 여전히 name(원본 문자열)이다. */
  displayName: string;
  /** 업체 원장에서 번호이동 수수료 미차감으로 설정했는지 */
  mnpFeeExempt: boolean;
  /** 연결된 정책 업체(policy_vendors) — 정책표를 뿌리는 도매상과 같은 회사임을 잇는 고리 */
  policyVendorId: number | null;
  /** 통신사 — 연결된 정책 업체에서 빌려온 값(DB 표기 SKT|KT|LGU), 연결이 없으면 null */
  carrier: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface AddonMaster {
  id: number;
  tenantId: string;
  name: string;
  monthlyFee: number;
  requiredMonths: number;
  /** 유지기간 계산식("M+2"·"D+95"). 이 부가서비스를 쓰는 모든 신청서가 따른다. 없으면 null. */
  requiredExpr: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface WelfareMaster {
  id: number;
  tenantId: string;
  name: string;
  code: string | null;
  sortOrder: number;
  isSystem: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface MemoSnippet {
  id: number;
  tenantId: string;
  kind: "verbalNote" | "deductionNote" | "otherSubsidyNote";
  label: string;
  body: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

// 이름만 가지는 단순 마스터 (보험·알뜰폰 사업자 등). add/edit/delete 만 지원.
export interface SimpleMaster {
  id: number;
  tenantId: string;
  name: string;
  /** 상품구분 전용 — 라인업 원본에서 전 매장 공유를 켠 값. */
  isShared?: boolean;
  /** 상품구분 전용 — 현재 매장 것이 아니라 합쳐 내려온 수정 불가 공통값. */
  isCommon?: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

/** 매장이 직접 추가한 요금제. null은 기존 행 또는 아직 월정액을 적지 않은 행이다. */
export interface PlanMaster extends SimpleMaster {
  monthlyFee: number | null;
}

// 고객유형 마스터 (로드·약식·단순방문·가망고객 등).
// isProspect=true 면 "가망(비개통) 건" — 개통대기 현황에서 하단 가망고객 리스트로 분리.
// sortOrder 는 매장이 직접 조정하는 우선순위(리스트·드롭다운 정렬 기준).
export interface CustomerTypeMaster {
  id: number;
  tenantId: string;
  name: string;
  isProspect: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type MasterItem =
  | DealerMaster
  | AddonMaster
  | WelfareMaster
  | MemoSnippet
  | CustomerTypeMaster
  | PlanMaster
  | SimpleMaster;

export async function listMasterData(kind: MasterKind): Promise<MasterItem[]> {
  const { items } = await fetchJson<{ items: MasterItem[] }>(
    `${API_V2}/crm/master/${kind}`,
  );
  return items;
}

export async function createMasterData(
  kind: MasterKind,
  input: Record<string, unknown>,
): Promise<MasterItem> {
  const { item } = await fetchJson<{ item: MasterItem }>(
    `${API_V2}/crm/master/${kind}`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return item;
}

export async function updateMasterData(
  kind: MasterKind,
  id: number,
  input: Record<string, unknown>,
): Promise<MasterItem> {
  const { item } = await fetchJson<{ item: MasterItem }>(
    `${API_V2}/crm/master/${kind}/${id}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return item;
}

export async function deleteMasterData(kind: MasterKind, id: number): Promise<void> {
  await fetchJson(`${API_V2}/crm/master/${kind}/${id}`, { method: "DELETE" });
}

export interface MasterUsage {
  kind: MasterKind;
  id: number;
  name: string | null;
  applicationCount: number;
  inDefaults: boolean;
  /** dealer 만 — 옛 이름으로 남은 정산 계산서 발행확인 기록 수 */
  invoiceConfirmationCount?: number;
  /** dealer 만 — «개통대리점 = / ≠ 이 이름» 조건을 가진 문자 규칙 수(이름을 바꾸면 함께 바뀐다) */
  messageRuleCount?: number;
  /** dealer 만 — «개통대리점에 ○○ 포함» 조건 문자 규칙 수(자동으로 안 바뀐다) */
  containsRuleCount?: number;
}

/** 대리점 이름을 바꾸기 전 «함께 바뀌는 곳» 수 — 업체 관리 화면의 확인창이 쓴다. */
export interface DealerRenameUsage {
  applicationCount: number;
  invoiceConfirmationCount: number;
  inDefaults: boolean;
  messageRuleCount: number;
  containsRuleCount: number;
}

/** 대리점 이름을 바꿀 때 서버가 함께 바꾼 곳(티켓 #1269). */
export interface DealerRenameResult {
  applications: number;
  invoiceConfirmations: number;
  formDefaults: boolean;
  messageRules: number;
}

/** 대리점 행 수정 — 이름이 바뀌면 신청서·정산 계산서 확인·매장 기본값·문자 규칙의
 *  옛 이름도 서버가 함께 바꾸고 무엇을 바꿨는지 돌려준다. */
export async function updateDealerMaster(
  id: number,
  input: { name?: string; alias?: string | null },
): Promise<{ item: DealerMaster; renamed?: DealerRenameResult }> {
  return fetchJson<{ item: DealerMaster; renamed?: DealerRenameResult }>(
    `${API_V2}/crm/master/dealer/${id}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
}

export async function getMasterUsage(
  kind: MasterKind,
  id: number,
): Promise<MasterUsage> {
  return fetchJson<MasterUsage>(`${API_V2}/crm/master/${kind}/${id}/usage`);
}

// ─── 대리점별 부가서비스 ──────────────────────────────────────────
//
// addon 마스터(매장 전역)와 달리 개통대리점 하나에 매단 부가서비스.
// 가입개통정보의 가입/미가입 부가서비스가 이 목록을 기준으로 토글된다(디폴트 전부 미가입).

export interface DealerAddon {
  id: number;
  tenantId: string;
  dealerId: number;
  /** 통신사(칩 표시·구분용). */
  carrier: CrmCarrier;
  /** 출처 크롤링 카탈로그 항목 id(`SKT_123`). 수동 항목이면 null. */
  xeronoteAddonId: string | null;
  name: string;
  /** 월요금(원). 카탈로그에서 복사. */
  monthlyFee: number;
  /** 시세표와 신청서 칩에 쓰는 판매점 직접 입력 요금 문구. 없으면 monthlyFee로 자동 생성한다. */
  feeNote: string | null;
  /** 이 부가서비스를 미가입했을 때 단말기에 더 붙는 추가금(원). 대리점이 입력. */
  deviceSurcharge: number;
  /** 유지기간 식("M+2"·"D+95"). 칩 배지로 표시·편집. 해지가능일 = 식 + 신청서 개통일. */
  requiredExpr: string | null;
  /** 보험성 부가서비스 여부. 가입하면 신청서 "보험" 칸에 자동으로 모여 표시된다. */
  isInsurance: boolean;
  /**
   * 이 부가서비스를 앞세울 단말기 갈래들("iphone"·"foldflip"·"android").
   * 신청서 [보험] 칸의 [단말기별 보험 매칭] 창에서 매장이 지정한다(스크린챗 #525).
   */
  deviceClasses?: string[] | null;
  /** 갈래와 무관하게 이 모델 이름에만 따로 거는 "예외 단말기"들. 갈래 지정보다 세다. */
  deviceModels?: string[] | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export async function listDealerAddons(dealerId: number): Promise<DealerAddon[]> {
  const { items } = await fetchJson<{ items: DealerAddon[] }>(
    `${API_V2}/crm/dealer-addons/${dealerId}`,
  );
  return items;
}

export async function createDealerAddon(
  dealerId: number,
  input: {
    carrier: CrmCarrier;
    name: string;
    monthlyFee?: number;
    deviceSurcharge?: number;
    xeronoteAddonId?: string | null;
    requiredExpr?: string | null;
    isInsurance?: boolean;
  },
): Promise<DealerAddon> {
  const { item } = await fetchJson<{ item: DealerAddon }>(
    `${API_V2}/crm/dealer-addons/${dealerId}`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return item;
}

export async function updateDealerAddon(
  dealerId: number,
  id: number,
  input: Partial<{ name: string; monthlyFee: number; deviceSurcharge: number; requiredExpr: string | null; isInsurance: boolean; sortOrder: number }>,
): Promise<DealerAddon> {
  const { item } = await fetchJson<{ item: DealerAddon }>(
    `${API_V2}/crm/dealer-addons/${dealerId}/${id}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return item;
}

/** 단말기별 보험 매칭 표를 통째로 저장한다(스크린챗 #525 — 창의 저장 버튼 한 번에 한 요청).
 *  items 에 없는 부가서비스는 짝이 지워진 것으로 처리되므로 **항상 표 전체**를 보낸다. */
export async function saveDealerAddonDeviceMatch(
  dealerId: number,
  items: Array<{ id: number; deviceClasses: string[]; deviceModels: string[] }>,
): Promise<DealerAddon[]> {
  const res = await fetchJson<{ items: DealerAddon[] }>(
    `${API_V2}/crm/dealer-addons/${dealerId}/device-match`,
    { method: "POST", body: JSON.stringify({ items }) },
  );
  return res.items;
}

export async function deleteDealerAddon(
  dealerId: number,
  id: number,
): Promise<void> {
  await fetchJson(`${API_V2}/crm/dealer-addons/${dealerId}/${id}`, {
    method: "DELETE",
  });
}

/** 한 대리점에서 고른 부가서비스들을 다른 대리점 여러 곳에 한 번에 복사한다.
 *  대상 대리점에 이미 같은 항목이 있으면 건너뛰며, 대리점별 추가/건너뜀 개수를 돌려준다. */
export interface CopyDealerAddonsResult {
  dealerId: number;
  added: number;
  skipped: number;
}
export async function copyDealerAddons(
  sourceDealerId: number,
  input: { addonIds: number[]; targetDealerIds: number[] },
): Promise<{ copied: number; results: CopyDealerAddonsResult[] }> {
  return fetchJson<{ copied: number; results: CopyDealerAddonsResult[] }>(
    `${API_V2}/crm/dealer-addons/${sourceDealerId}/copy-to`,
    { method: "POST", body: JSON.stringify(input) },
  );
}

// ─── 단말기 모델 카탈로그 (closed-set) ─────────────────────────

// 단말 종류. 드롭다운·목록에서 칩(폰/패드/워치/와이파이)으로 구분 표시한다.
export type DeviceCategory = "phone" | "tablet" | "watch" | "wifi";

export const DEVICE_CATEGORY_LABEL: Record<DeviceCategory, string> = {
  phone: "폰",
  tablet: "패드",
  watch: "워치",
  wifi: "와이파이",
};

export interface PhoneModel {
  id: string;
  name: string;
  code: string;
  manufacturer: string | null;
  series: string;
  lineup: string | null;
  category?: DeviceCategory; // 폰/패드/워치/와이파이 (구버전 응답 호환 위해 optional)
  releasePrice?: number; // 출고가(원). 정산 화면 자동채움용 (task 266)
  // 출고가 자동갱신 사각지대 여부 — 제로노트가 이 모델을 안 실어 크롤링이 출고가를 못 갱신 중.
  // true 면 자동채움된 출고가가 옛 값일 수 있어 개통 화면에서 "출고가 확인 필요" 안내를 띄운다.
  priceStale?: boolean;
  /** 표준명과 다른 통신사별 판매 이름. 검색·표시에서 함께 쓰고, 비어 있으면 표준명으로 폴백한다. */
  carrierNames?: Partial<Record<"SK" | "KT" | "LG", string>>;
  defaultSortOrder: number;
}

export async function listPhoneModels(): Promise<PhoneModel[]> {
  const { items } = await fetchJson<{ items: PhoneModel[] }>(
    `${API_V2}/crm/models`,
  );
  return items;
}

// ─── 요금제 카탈로그 + 공시지원금 (task 266, 제로노트 적재) ──────────────

export interface PhonePlan {
  id: string;
  carrier: CrmCarrier;
  name: string;
  monthlyPrice: number; // 월정액(원)
  /** 통신사가 신규 가입을 막은 구요금제인가(티켓 #830). 「요금제군 한눈에」에서 지정한다.
   *  거의 안 바뀌는 값이라 **이 목록 응답에 함께 실려** 오고, 드롭다운은 따로 물어보지 않는다. */
  registrationRestricted?: boolean;
}

export type SubsidyJoinType = "new_join" | "mnp" | "device_change";

/** 통신사별 요금제 목록(검색 드롭다운용). 월정액 오름차순. (인증) */
export async function listPlansByCarrier(carrier: CrmCarrier): Promise<PhonePlan[]> {
  const { items } = await fetchJson<{ items: PhonePlan[] }>(
    `${API_V2}/crm/plans?carrier=${encodeURIComponent(carrier)}`,
  );
  return items;
}

/** 카드·유심·복지·가입비의 현재 제로노트 선택지. 영역별 실패는 unavailable 로 받는다. */
export async function fetchWirelessAutofillOptions(
  carrier: CrmCarrier,
): Promise<WirelessConsultOptions> {
  return fetchJson<WirelessConsultOptions>(
    `${API_V2}/crm/autofill-options?carrier=${encodeURIComponent(carrier)}`,
  );
}

// ─── 부가서비스 카탈로그 (task 267, 제로노트 적재) ──────────────

export interface XeronoteAddon {
  id: string;
  carrier: CrmCarrier;
  name: string;
  serviceType: string | null;
  monthlyFee: number; // 월 요금(원)
  requiredExpr: string | null; // 유지기간 식(대부분 null)
}

/** 통신사별 부가서비스 카탈로그(검색 드롭다운용). q 있으면 이름 부분검색. (인증) */
export async function listAddonsByCarrier(
  carrier: CrmCarrier,
  q?: string,
): Promise<XeronoteAddon[]> {
  const qs = new URLSearchParams({ carrier });
  if (q && q.trim()) qs.set("q", q.trim());
  const { items } = await fetchJson<{ items: XeronoteAddon[] }>(
    `${API_V2}/crm/addons?${qs}`,
  );
  return items;
}

/**
 * 전 통신사 부가서비스 카탈로그 전체 — 대리점별 부가서비스 연결 화면용.
 * 통신사 구분 없이 한 목록에서 고르고, 항목마다 carrier 칩을 단다. 카탈로그는 수백 건 수준이라
 * 검색어 없이 한 번에 다 받아 두고, 검색은 프론트에서 즉시 필터한다(서버 왕복 없음).
 */
export async function fetchAddonCatalog(): Promise<XeronoteAddon[]> {
  const { items } = await fetchJson<{ items: XeronoteAddon[] }>(
    `${API_V2}/crm/addons`,
  );
  return items;
}

// ─── 보험 카탈로그 (제로노트 적재) ──────────────

export interface XeronoteInsurance {
  id: string;
  carrier: CrmCarrier;
  name: string;
  targetDevice: string | null; // 가입 대상 단말 설명(출고가 구간·기종 등)
  monthlyFee: number; // 월 보험료(원)
  compensationType: string | null; // 보장 종류(파손·분실·교체 등)
  selfSharing: string | null; // 자기부담금 설명
}

/** 통신사별 보험 카탈로그(검색 드롭다운용). q 있으면 이름·대상기기 부분검색. (인증) */
export async function listInsurancesByCarrier(
  carrier: CrmCarrier,
  q?: string,
): Promise<XeronoteInsurance[]> {
  const qs = new URLSearchParams({ carrier });
  if (q && q.trim()) qs.set("q", q.trim());
  const { items } = await fetchJson<{ items: XeronoteInsurance[] }>(
    `${API_V2}/crm/insurances?${qs}`,
  );
  return items;
}

/**
 * 전 통신사 보험 카탈로그 전체 — 대리점별 부가서비스 연결 화면(보험 탭)용.
 * fetchAddonCatalog 와 같은 패턴: 검색어 없이 한 번에 다 받아 두고 프론트에서 즉시 필터.
 */
export async function fetchInsuranceCatalog(): Promise<XeronoteInsurance[]> {
  const { items } = await fetchJson<{ items: XeronoteInsurance[] }>(
    `${API_V2}/crm/insurances`,
  );
  return items;
}

/** 공개폼(비로그인)용 요금제 목록 — 통신사별. 토큰 검증 후 내려받음. */
export async function fetchPublicFormPlans(
  token: string,
  carrier: string,
): Promise<PhonePlan[]> {
  const res = await publicFetch(
    `${API_V2}/public-forms/${token}/plans?carrier=${encodeURIComponent(carrier)}`,
    { headers: await publicFormHeaders() },
  );
  if (!res.ok) throw await apiError(res);
  const { items } = (await res.json()) as { items: PhonePlan[] };
  return items;
}

/**
 * 공시지원금 조회 — (단말 × 통신사 × 요금제 월정액 × 가입유형). 없으면 null. (인증)
 * asOf(YYYY-MM-DD)를 주면 그 날짜에 유효한 값(개통일 기준)을 받는다. 없으면 최신값.
 */
export async function lookupSubsidy(args: {
  modelId: string;
  carrier: CrmCarrier;
  monthlyPrice: number;
  joinType: SubsidyJoinType;
  asOf?: string;
}): Promise<number | null> {
  const qs = new URLSearchParams({
    modelId: args.modelId,
    carrier: args.carrier,
    monthlyPrice: String(args.monthlyPrice),
    joinType: args.joinType,
  });
  if (args.asOf) qs.set("asOf", args.asOf); // 값 있을 때만 — undefined 가 문자열로 박히는 함정 회피
  const { amount } = await fetchJson<{ amount: number | null }>(
    `${API_V2}/crm/subsidy?${qs}`,
  );
  return amount;
}

/**
 * 출고가 조회(개통일 기준) — 크롤링 일일 스냅샷에서 asOf(YYYY-MM-DD) 당시 값을 찾는다. (인증)
 * 기록이 없으면(스냅샷 보존 90일 초과 등) price: null — 호출 쪽이 최신값+안내 문구로 폴백한다.
 * 개통 신청서 수정 모달의 출고가 자동채움용 (티켓 #432).
 */
export async function lookupReleasePriceAsOf(args: {
  modelId: string;
  asOf: string;
}): Promise<{ price: number | null; snapshotDate: string | null }> {
  const qs = new URLSearchParams({ modelId: args.modelId, asOf: args.asOf });
  return await fetchJson<{ price: number | null; snapshotDate: string | null }>(
    `${API_V2}/crm/release-price?${qs}`,
  );
}

// ─── 정산 근거 (신청서 1:N) ─────────────────────────────────────────
//
// 정산할 때 적용한 정책(액면·구두)을 그 시점에 박제해 신청서에 붙여둔다. 액면/구두를 나눠
// 받고, 캡처 이미지(카톡·엑셀 스크린샷)는 S3 키 배열로 보관한다. 자동조회로 추천값을 끌어와도
// 사람이 수정·확정한다. (서버: server/src/v2/routes/crm/settlement-evidences.ts)

/** 금액은 원 단위. 액면/구두 둘 다 nullable — 한쪽만 적을 수도 있다. */
export interface SettlementEvidence {
  id: number;
  faceAmount: number | null;
  verbalAmount: number | null;
  vendorName: string | null;
  modelName: string | null;
  planName: string | null;
  activationType: string | null;
  memo: string | null;
  imageKeys: string[];
  imageUrls: string[]; // 조회 시 재서명된 presigned URL (imageKeys 와 같은 순서)
  source: "manual" | "auto";
  snapshotAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface SettlementEvidenceInput {
  faceAmount?: number | null;
  verbalAmount?: number | null;
  vendorName?: string | null;
  modelName?: string | null;
  planName?: string | null;
  activationType?: string | null;
  memo?: string | null;
  imageKeys?: string[];
  source?: "manual" | "auto";
}

/** 구두 한 항목 — 계산 근거 표시용. 금액은 원 단위(서버가 만원→원 변환). */
export interface SettlementVerbalItem {
  modelRaw: string;
  planRange: string;
  dealType: string;
  amount: number;
  entryId: number;
  seq: number; // 그날 구두 순번(구두N)
  name: string | null; // 출처 정책 이름(없으면 "구두N" 폴백)
}

/** 자동조회 결과 — 신청서 키를 정책 표준키로 변환해 시세표에서 끌어온 추천. 금액은 원 단위. */
export interface RebateAmount {
  vendorName: string;
  face: number;
  verbal: number;
  total: number;
  release: number | null; // 출고가(원) — 액면 출처 가늠
  verbalItems: SettlementVerbalItem[]; // 구두 분해(합산 = 액면 + Σ구두)
}
export interface SettlementSuggestion {
  status: "ok" | "no-model" | "no-rebate";
  date: string;
  resolved: {
    carrier: string;
    modelStd: string | null;
    modelName: string | null;
    modelSource: "id" | "name" | null;
    planStd: string | null;
    planLabel: string | null;
    dealType: string;
    vendorName: string | null; // 개통대리점 → 매칭된 정책 업체명
  };
  candidates: RebateAmount[];
  best: RebateAmount | null;
  matched: RebateAmount | null; // 매칭된 업체의 리베 (있으면 best 보다 우선)
}

export async function fetchSettlementEvidence(
  applicationFormId: number,
): Promise<SettlementEvidence[]> {
  const { items } = await fetchJson<{ items: SettlementEvidence[] }>(
    `${API_V2}/crm/applications/${applicationFormId}/settlement-evidence`,
  );
  return items;
}

export async function createSettlementEvidence(
  applicationFormId: number,
  input: SettlementEvidenceInput,
): Promise<number> {
  const { id } = await fetchJson<{ id: number }>(
    `${API_V2}/crm/applications/${applicationFormId}/settlement-evidence`,
    { method: "POST", body: JSON.stringify(input) },
  );
  return id;
}

export async function updateSettlementEvidence(
  applicationFormId: number,
  evidenceId: number,
  input: SettlementEvidenceInput,
): Promise<void> {
  await fetchJson(
    `${API_V2}/crm/applications/${applicationFormId}/settlement-evidence/${evidenceId}`,
    { method: "PATCH", body: JSON.stringify(input) },
  );
}

export async function deleteSettlementEvidence(
  applicationFormId: number,
  evidenceId: number,
): Promise<void> {
  await fetchJson(
    `${API_V2}/crm/applications/${applicationFormId}/settlement-evidence/${evidenceId}`,
    { method: "DELETE" },
  );
}

/** 캡처 이미지를 S3 에 올리고 영구 키와 미리보기 URL 을 받는다. 키는 생성/수정에 실어 저장한다. */
export async function uploadSettlementEvidenceImage(
  applicationFormId: number,
  file: File,
): Promise<{ key: string; url: string }> {
  const form = new FormData();
  form.append("image", file, file.name || "capture.png");
  return authForm<{ key: string; url: string }>(
    `${API_V2}/crm/applications/${applicationFormId}/settlement-evidence/image`,
    form,
  );
}

export async function fetchSettlementSuggest(
  applicationFormId: number,
): Promise<SettlementSuggestion> {
  return fetchJson<SettlementSuggestion>(
    `${API_V2}/crm/applications/${applicationFormId}/settlement-suggest`,
  );
}

// ─── 개통 신청서 서류함 (스크린챗 티켓 #4) ───────────────────────────
//
// 개통 건에 첨부한 종이 서류(스캔본)를 종류별로 여러 장 담는다. 파일 한 장이 한 서류(행)이고,
// 파일명·저장 위치는 서버가 정한다(직원이 이름 짓기/폴더 고르기를 안 하게 하는 게 목적).

export interface ApplicationDocument {
  id: number;
  docType: string;
  fileName: string;
  contentType: string; // image/jpeg | image/png | application/pdf
  sizeBytes: number;
  url: string | null; // 조회 시 재서명된 다운로드용 presigned URL (키 유실 시 null)
  createdAt: string;
}

/** 그 신청서의 서류 목록 */
export async function fetchApplicationDocuments(
  applicationFormId: number,
): Promise<ApplicationDocument[]> {
  const { items } = await fetchJson<{ items: ApplicationDocument[] }>(
    `${API_V2}/crm/applications/${applicationFormId}/documents`,
  );
  return items;
}

/** 서류 1장 업로드(multipart). 서버가 파일명·키를 정하고 생성된 서류를 돌려준다. */
export async function uploadApplicationDocument(
  applicationFormId: number,
  file: File,
  docType: string,
): Promise<ApplicationDocument> {
  const form = new FormData();
  form.append("file", file, file.name || "document");
  form.append("docType", docType);
  return authForm<ApplicationDocument>(
    `${API_V2}/crm/applications/${applicationFormId}/documents`,
    form,
  );
}

/** 서류 삭제 (soft delete + S3 정리) */
export async function deleteApplicationDocument(
  applicationFormId: number,
  documentId: number,
): Promise<void> {
  await fetchJson(
    `${API_V2}/crm/applications/${applicationFormId}/documents/${documentId}`,
    { method: "DELETE" },
  );
}

/** 매장의 서류 종류 목록 */
export async function fetchDocumentTypes(): Promise<string[]> {
  const { types } = await fetchJson<{ types: string[] }>(
    `${API_V2}/crm/document-types`,
  );
  return types;
}

/** 매장의 서류 종류 목록 저장 → 저장 후 목록 반환 */
export async function updateDocumentTypes(types: string[]): Promise<string[]> {
  const res = await fetchJson<{ types: string[] }>(
    `${API_V2}/crm/document-types`,
    { method: "PUT", body: JSON.stringify({ types }) },
  );
  return res.types;
}
